import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './StallionStatus.css'; // Reuse the same styles
import { CircularProgress } from '@mui/material';

const API_URL = import.meta.env.VITE_API_URL || '';
const API_TOKEN = 'Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq';

const StallionReconcile = () => {
  const [shipCodeInput, setShipCodeInput] = useState('');
  const [allShipments, setAllShipments] = useState([]);
  const [matchedShipments, setMatchedShipments] = useState([]);
  const [unmatchedCodes, setUnmatchedCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [expandedShipments, setExpandedShipments] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [asinToSkuCache, setAsinToSkuCache] = useState({}); // Cache for ASIN to SKU mappings per client
  const [showAsinSummary, setShowAsinSummary] = useState(false); // Toggle for ASIN summary view
  const [expandedAsins, setExpandedAsins] = useState(new Set()); // Track expanded ASINs in summary
  const [showAsinFilter, setShowAsinFilter] = useState(false); // Toggle for ASIN filter view
  const [asinFilterResults, setAsinFilterResults] = useState(null); // Results from ASIN filtering
  const [exportShipCodes, setExportShipCodes] = useState(''); // Ship codes for comprehensive export
  const [isExporting, setIsExporting] = useState(false); // Export loading state

  // LocalStorage cache keys
  const CACHE_KEY = 'stallion_reconcile_data';
  const CACHE_TIMESTAMP_KEY = 'stallion_reconcile_timestamp';
  const CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

  // ASIN lists for filtering
  const NON_FDA_ASINS = [
    'B00P1K5MM0', 'B01MXJWEVB', 'B01BF6F20K', 'B0007UPZZ8', 'B005MQZL0Q',
    'B071Z738R6', 'B09S3Y2S7F', 'B07RJKRKFM', 'B00BWOFW88', 'B09NLC4KF7',
    'B004MAP112', '1953337678', 'B06XCQ6891', 'B07P91B8J1', 'B097M2RWQ7',
    'B0BW6GCPLD', 'B00CMM923A', 'B071NB2BV3', 'B09H1FRF5S', 'B0CRJ1FXVV'
  ];

  const FDA_ASINS = [
    'B00BIWZ5D6', 'B01B7EXEJ6', 'B01BF6F20K', 'B06XDMDTSK', 'B07LCH8186',
    'B08PQC5ZY8', 'B08VCJR5YH', 'B0CXZYHC3M', 'B09KT1DXWS', 'B09KT7NBZ7',
    'B0CD86C6RX', 'B0CDYPFY3Y', 'B0CK3VHZZB', 'B0CQK18QKN', 'B0D5NLHJ5R',
    'B0D5RHMKQC', 'B0DJCBV6PM', 'B001F9Y9FC', '1950000000', 'B000FGDIRQ',
    'B00F12JPW6', 'B00I8WH8CK', 'B07LGHCHJ9', 'B0BK2J4Y8F', 'B0BW6GCPLD'
  ];

  // Load cached data or fetch new data on component mount
  useEffect(() => {
    loadShipmentsData();
  }, []);

  const loadShipmentsData = () => {
    const cachedData = getCachedData();
    if (cachedData && Array.isArray(cachedData.data)) {
      console.log('Loading shipments from localStorage cache');
      setAllShipments(cachedData.data);
      setLastRefreshed(new Date(cachedData.timestamp));
      setIsInitialLoad(false);
    } else {
      console.log('No valid cache found, fetching fresh data');
      fetchAllShipments();
    }
  };

  const getCachedData = () => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (!cachedData || !cachedTimestamp) {
        return null;
      }

      const timestamp = parseInt(cachedTimestamp);
      const now = Date.now();
      
      // Check if cache is still valid (less than 2 hours old)
      if (now - timestamp > CACHE_DURATION) {
        console.log('Cache expired, removing old data');
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_TIMESTAMP_KEY);
        return null;
      }

      return {
        data: JSON.parse(cachedData),
        timestamp: timestamp
      };
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      return null;
    }
  };

  const setCachedData = (data) => {
    try {
      const timestamp = Date.now();
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, timestamp.toString());
      console.log(`Cached ${data.length} shipments to localStorage`);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      // If localStorage is full or unavailable, continue without caching
    }
  };

  const clearCache = () => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    console.log('Cleared localStorage cache');
  };

  const fetchAllShipments = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_URL}/api/stallion-reconcile-shipments`);
      const shipmentData = response.data;

      // Ensure shipmentData is an array
      const shipmentsArray = Array.isArray(shipmentData) ? shipmentData : [];

      // Cache the data in localStorage
      setCachedData(shipmentsArray);

      setAllShipments(shipmentsArray);
      setLastRefreshed(new Date());
      setIsInitialLoad(false);

      console.log(`Loaded ${shipmentsArray.length} shipments`);
    } catch (err) {
      setError(`Failed to fetch shipments: ${err.message}`);
      setAllShipments([]); // Set to empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    clearCache(); // Clear localStorage cache to force fresh data
    fetchAllShipments();
  };

  const parseShipCodes = (input) => {
    if (!input.trim()) return [];
    
    // Split by comma, semicolon, or newline, then clean up
    return input
      .split(/[,;\n]/)
      .map(code => code.trim())
      .filter(code => code.length > 0);
  };

  const handleReconcile = () => {
    const inputCodes = parseShipCodes(shipCodeInput);
    if (inputCodes.length === 0) {
      setError('Please enter some ship codes to reconcile');
      return;
    }

    // Ensure allShipments is an array
    if (!Array.isArray(allShipments) || allShipments.length === 0) {
      setError('No shipment data available. Please refresh the data.');
      return;
    }

    const matched = [];
    const unmatched = [];

    // Create a map of ship codes to shipments for faster lookup
    const shipmentMap = new Map();
    allShipments.forEach(shipment => {
      if (shipment.ship_code) {
        shipmentMap.set(shipment.ship_code.toLowerCase(), shipment);
      }
    });

    // Check each input code
    inputCodes.forEach(code => {
      const normalizedCode = code.toLowerCase();
      const shipment = shipmentMap.get(normalizedCode);
      
      if (shipment) {
        matched.push(shipment);
      } else {
        unmatched.push(code);
      }
    });

    setMatchedShipments(matched);
    setUnmatchedCodes(unmatched);
    setError(null);
  };

  const handleClear = () => {
    setShipCodeInput('');
    setMatchedShipments([]);
    setUnmatchedCodes([]);
    setExpandedShipments(new Set());
    setError(null);
  };

  const toggleShipmentExpansion = (shipmentCode) => {
    const newExpanded = new Set(expandedShipments);
    if (newExpanded.has(shipmentCode)) {
      newExpanded.delete(shipmentCode);
    } else {
      newExpanded.add(shipmentCode);
    }
    setExpandedShipments(newExpanded);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  const formatCurrency = (value, currency = 'USD') => {
    if (value === undefined || value === null) return 'N/A';
    // All values are in USD, so always format as USD regardless of currency parameter
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };


  const getItemsWithOrigins = (items) => {
    if (!items || items.length === 0) return [];
    
    return items.map(item => {
      return {
        sku: item.sku || 'N/A',
        description: item.description || 'No description',
        quantity: item.quantity || 0,
        value: formatCurrency(item.value), // All values are USD
        origin: item.country_of_origin || 'Unknown',
        hsCode: item.hs_code || 'N/A'
      };
    });
  };

  const normalizeAddress = (address1, city, province, postal) => {
    const components = [
      address1 ? address1.trim() : 'Unknown Address',
      city ? city.trim() : '',
      province ? province.trim() : '',
      postal ? postal.trim() : ''
    ].filter(Boolean);
    
    return components.join(', ');
  };

  const getShipmentsGroupedByClient = () => {
    // First sort shipments
    const sorted = [...matchedShipments].sort((a, b) => {
      if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;
      if (!a[sortConfig.key]) return 1;
      if (!b[sortConfig.key]) return -1;

      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Special handling for dates
      if (sortConfig.key === 'created_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Special handling for numeric values
      if (sortConfig.key === 'weight' || sortConfig.key === 'value' || 
          sortConfig.key === 'length' || sortConfig.key === 'width' || 
          sortConfig.key === 'height') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    // Group by client
    const grouped = sorted.reduce((groups, shipment) => {
      const client = shipment.clientName || 'Unknown Client';
      if (!groups[client]) {
        groups[client] = [];
      }
      groups[client].push(shipment);
      return groups;
    }, {});

    // Sort clients alphabetically
    return Object.keys(grouped)
      .sort()
      .map(clientName => ({
        clientName,
        shipments: grouped[clientName],
        count: grouped[clientName].length,
        totalValue: grouped[clientName].reduce((sum, ship) => sum + (parseFloat(ship.value) || 0), 0)
      }));
  };

  // Function to get client ID from client name
  const getClientId = async (clientName) => {
    try {
      const response = await fetch(`${API_URL}/api/get_clients`);
      if (!response.ok) {
        throw new Error('Failed to fetch clients');
      }
      const clients = await response.json();
      // Match client by name (case insensitive and trim whitespace)
      const normalizedClientName = clientName.trim().toLowerCase();
      const client = clients.find(c => 
        c.name && c.name.trim().toLowerCase() === normalizedClientName
      );
      return client ? client.id : null;
    } catch (error) {
      console.error('Error fetching client ID:', error);
      return null;
    }
  };

  // Function to search inventory by ASIN and get SKU with caching
  const getSkuFromAsin = async (asin, clientId, trackNoHistory = false) => {
    // Check cache first
    const cacheKey = `${clientId}:${asin}`;
    if (asinToSkuCache[cacheKey]) {
      console.log(`Using cached SKU for ASIN ${asin}: ${asinToSkuCache[cacheKey]}`);
      return asinToSkuCache[cacheKey];
    }

    try {
      const response = await fetch(
        `https://app.yyzprep.ca/api/inventory?page=1&per_page=20&q=${asin}&sort_by=available_quantity&sort_dir=desc&api_token=${API_TOKEN}`,
        {
          headers: {
            'X-Selected-Client-Id': clientId.toString()
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to search inventory');
      }
      
      const data = await response.json();
      const items = data.data || [];
      
      // Find items that match the ASIN
      const matchingItems = items.filter(item => {
        return item.asin === asin || 
               item.searchableIdentifiers?.includes(asin) ||
               item.identifiers?.some(id => id.identifier === asin && id.identifier_type === 'ASIN');
      });
      
      if (matchingItems.length === 0) {
        console.log(`No items found matching ASIN: ${asin}`);
        // Cache the null result to avoid repeated searches for unknown ASINs
        setAsinToSkuCache(prev => ({ ...prev, [cacheKey]: null }));
        return null;
      }
      
      // If single match, cache and return it
      if (matchingItems.length === 1) {
        const sku = matchingItems[0].merchant_sku;
        console.log(`Single match found for ASIN ${asin}: ${sku}`);
        setAsinToSkuCache(prev => ({ ...prev, [cacheKey]: sku }));
        return sku;
      }
      
      console.log(`Multiple matches (${matchingItems.length}) found for ASIN ${asin}, checking history...`);
      
      // Multiple matches - dig deeper to find the most recent outbound shipment
      let bestItem = null;
      let mostRecentOutboundDate = null;
      let hasOutboundHistory = false;
      
      for (const item of matchingItems) {
        try {
          const detailResponse = await fetch(
            `https://app.yyzprep.ca/api/inventory/${item.id}?api_token=${API_TOKEN}`,
            {
              headers: {
                'X-Selected-Client-Id': clientId.toString()
              }
            }
          );
          
          if (!detailResponse.ok) continue;
          
          const detailData = await detailResponse.json();
          const history = detailData.item?.history || [];
          
          // Find the most recent outbound shipment
          const outboundShipments = history.filter(h => h.type === 'outbound_shipment');
          
          if (outboundShipments.length > 0) {
            hasOutboundHistory = true;
            const mostRecent = outboundShipments.reduce((latest, current) => 
              new Date(current.date) > new Date(latest.date) ? current : latest
            );
            
            if (!mostRecentOutboundDate || new Date(mostRecent.date) > new Date(mostRecentOutboundDate)) {
              mostRecentOutboundDate = mostRecent.date;
              bestItem = item;
              console.log(`Found item with outbound history for ASIN ${asin}: ${item.merchant_sku} (last outbound: ${mostRecent.date})`);
            }
          } else if (!bestItem && history.length > 0) {
            // Fallback to any item with history if no outbound shipments
            bestItem = item;
          }
        } catch (error) {
          console.error(`Error fetching details for item ${item.id}:`, error);
        }
      }
      
      // Log if no items had outbound history
      if (!hasOutboundHistory && matchingItems.length > 1) {
        console.warn(`Warning: Multiple SKUs found for ASIN ${asin} but none have outbound history. Using first match: ${matchingItems[0].merchant_sku}`);
      }
      
      // Cache and return the best match or the first one if no history found
      const finalSku = (bestItem || matchingItems[0]).merchant_sku;
      setAsinToSkuCache(prev => ({ ...prev, [cacheKey]: finalSku }));
      return finalSku;
      
    } catch (error) {
      console.error('Error searching for SKU:', error);
      // Cache null result for failed lookups to avoid repeated failures
      setAsinToSkuCache(prev => ({ ...prev, [cacheKey]: null }));
      return null;
    }
  };

  // Function to clear ASIN to SKU cache
  const clearAsinSkuCache = () => {
    setAsinToSkuCache({});
    console.log('ASIN to SKU cache cleared');
  };

  // Function to get cache statistics
  const getCacheStats = () => {
    const totalEntries = Object.keys(asinToSkuCache).length;
    const nullEntries = Object.values(asinToSkuCache).filter(val => val === null).length;
    const validEntries = totalEntries - nullEntries;
    return { totalEntries, validEntries, nullEntries };
  };

  // Function to export CSV in outbound format
  // Function to filter ship codes by specific ASINs
  const filterShipCodesByAsins = () => {
    // Ensure matchedShipments is an array
    if (!Array.isArray(matchedShipments) || matchedShipments.length === 0) {
      setAsinFilterResults({
        error: 'No matched shipments available. Please reconcile ship codes first.',
        nonFdaShipCodes: [],
        fdaShipCodes: []
      });
      return;
    }

    const nonFdaShipCodes = [];
    const fdaShipCodes = [];

    // Process only matched shipments from reconciliation
    matchedShipments.forEach(shipment => {
      if (shipment.items && Array.isArray(shipment.items) && shipment.items.length > 0) {
        // Check if any item in the shipment has a matching ASIN
        const shipmentAsins = shipment.items.map(item => item.sku).filter(Boolean);

        // Check for Non-FDA ASINs
        const hasNonFdaAsin = shipmentAsins.some(asin =>
          NON_FDA_ASINS.includes(asin.toUpperCase())
        );

        // Check for FDA ASINs
        const hasFdaAsin = shipmentAsins.some(asin =>
          FDA_ASINS.includes(asin.toUpperCase())
        );

        if (hasNonFdaAsin) {
          const matchingAsins = shipmentAsins.filter(asin =>
            NON_FDA_ASINS.includes(asin.toUpperCase())
          );
          nonFdaShipCodes.push({
            shipCode: shipment.ship_code,
            clientName: shipment.clientName || 'Unknown Client',
            matchingAsins: matchingAsins,
            allAsins: shipmentAsins,
            status: shipment.status,
            createdAt: shipment.created_at,
            trackingCode: shipment.tracking_code
          });
        }

        if (hasFdaAsin) {
          const matchingAsins = shipmentAsins.filter(asin =>
            FDA_ASINS.includes(asin.toUpperCase())
          );
          fdaShipCodes.push({
            shipCode: shipment.ship_code,
            clientName: shipment.clientName || 'Unknown Client',
            matchingAsins: matchingAsins,
            allAsins: shipmentAsins,
            status: shipment.status,
            createdAt: shipment.created_at,
            trackingCode: shipment.tracking_code
          });
        }
      }
    });

    // Sort by client name and ship code
    const sortResults = (results) => results.sort((a, b) => {
      const clientCompare = a.clientName.localeCompare(b.clientName);
      if (clientCompare !== 0) return clientCompare;
      return a.shipCode.localeCompare(b.shipCode);
    });

    setAsinFilterResults({
      error: null,
      nonFdaShipCodes: sortResults(nonFdaShipCodes),
      fdaShipCodes: sortResults(fdaShipCodes),
      searchDate: new Date()
    });
  };

  // Function to get ASIN summary with client breakdown
  const getAsinSummary = () => {
    const asinData = {};

    // Ensure matchedShipments is an array before processing
    if (!Array.isArray(matchedShipments) || matchedShipments.length === 0) {
      return [];
    }

    // Process all matched shipments
    matchedShipments.forEach(shipment => {
      const clientName = shipment.clientName || 'Unknown Client';

      if (shipment.items && Array.isArray(shipment.items) && shipment.items.length > 0) {
        shipment.items.forEach(item => {
          // The ASIN is stored in item.sku field from Stallion API
          const asin = item.sku || 'Unknown';
          const quantity = parseInt(item.quantity) || 1;
          const description = item.description || 'No description';

          if (!asinData[asin]) {
            asinData[asin] = {
              asin: asin,
              description: description,
              totalQuantity: 0,
              clients: {}
            };
          }

          // Update total quantity
          asinData[asin].totalQuantity += quantity;

          // Update client-specific quantity
          if (!asinData[asin].clients[clientName]) {
            asinData[asin].clients[clientName] = {
              quantity: 0,
              shipments: []
            };
          }
          asinData[asin].clients[clientName].quantity += quantity;

          // Track which shipments contain this ASIN for this client
          if (!asinData[asin].clients[clientName].shipments.includes(shipment.ship_code)) {
            asinData[asin].clients[clientName].shipments.push(shipment.ship_code);
          }
        });
      }
    });

    // Convert to array and sort by total quantity descending
    return Object.values(asinData).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  const exportOutboundCSV = async () => {
    if (!matchedShipments || matchedShipments.length === 0) {
      alert('No matched shipments to export');
      return;
    }

    try {
      // Show loading state
      setIsLoading(true);

      // Group shipments by client and process each client
      const groupedShipments = getShipmentsGroupedByClient();
      
      const boxContentsData = [];
      const shipmentItemsData = [];
      const unmappedItems = []; // Track items that couldn't be mapped to SKUs
      const noHistoryItems = []; // Track items without outbound history
      
      for (const clientGroup of groupedShipments) {
        console.log(`Processing client: ${clientGroup.clientName}`);
        
        // Get client ID
        const clientId = await getClientId(clientGroup.clientName);
        if (!clientId) {
          console.warn(`Could not find client ID for ${clientGroup.clientName}`);
          unmappedItems.push({
            client: clientGroup.clientName,
            reason: 'Client not found in database'
          });
          continue;
        }

        let boxNumber = 1;
        
        for (const shipment of clientGroup.shipments) {
          console.log(`Processing shipment: ${shipment.ship_code}`);
          
          if (!shipment.items || shipment.items.length === 0) {
            // Create empty box entry if no items
            boxContentsData.push({
              BOX_NUMBER: boxNumber,
              SKU: '',
              QTY: 0,
              ASIN: '',
              FNSKU: '',
              LENGTH: shipment.length || 0,
              WIDTH: shipment.width || 0,
              HEIGHT: shipment.height || 0,
              WEIGHT: shipment.weight || 0
            });
            boxNumber++;
            continue;
          }

          // Process each item in the shipment
          for (const item of shipment.items) {
            // The ASIN is stored in item.sku field from Stallion API
            const asin = item.sku || 'Unknown';
            
            // Get SKU from ASIN
            console.log(`Looking up SKU for ASIN: ${asin}`);
            const sku = await getSkuFromAsin(asin, clientId);
            
            // Track if SKU couldn't be found
            if (!sku) {
              unmappedItems.push({
                client: clientGroup.clientName,
                shipCode: shipment.ship_code,
                asin: asin,
                quantity: item.quantity || 1,
                description: item.description || 'No description'
              });
            }
            
            const finalSku = sku || asin; // Fallback to ASIN if SKU not found
            
            // Add to box contents (each shipment is a box)
            boxContentsData.push({
              BOX_NUMBER: boxNumber,
              SKU: finalSku,
              QTY: item.quantity || 1,
              ASIN: asin,
              FNSKU: item.fnsku || '',
              LENGTH: shipment.length || 0,
              WIDTH: shipment.width || 0,  
              HEIGHT: shipment.height || 0,
              WEIGHT: shipment.weight || 0
            });

            // Add to shipment items (unique items only)
            const existingItem = shipmentItemsData.find(si => si.SKU === finalSku);
            if (!existingItem) {
              shipmentItemsData.push({
                SKU: finalSku,
                QTY: item.quantity || 1,
                ASIN: asin,
                FNSKU: item.fnsku || '',
                TITLE: item.description || asin || 'Unknown Item',
                EXPIRY_DATE: '',
                SERVICES_ADDED: '',
                LENGTH: item.length_mm ? (item.length_mm / 25.4).toFixed(2) : '',
                WIDTH: item.width_mm ? (item.width_mm / 25.4).toFixed(2) : '',
                HEIGHT: item.height_mm ? (item.height_mm / 25.4).toFixed(2) : '',
                WEIGHT: item.weight_gm ? (item.weight_gm / 453.592).toFixed(2) : ''
              });
            } else {
              // Update quantity if item already exists
              existingItem.QTY += (item.quantity || 1);
            }
          }
          
          boxNumber++;
        }
      }

      // Create workbook and worksheets
      const XLSX = await import('xlsx');
      const workbook = XLSX.utils.book_new();

      // Add Box Contents sheet
      const boxContentsSheet = XLSX.utils.json_to_sheet(boxContentsData);
      XLSX.utils.book_append_sheet(workbook, boxContentsSheet, 'Box Contents');

      // Add Shipment Items sheet
      const shipmentItemsSheet = XLSX.utils.json_to_sheet(shipmentItemsData);
      XLSX.utils.book_append_sheet(workbook, shipmentItemsSheet, 'Shipment Items');

      // Generate filename
      const dateStr = new Date().toISOString().slice(0, 10);
      const filename = `stallion_reconcile_export_${dateStr}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

      console.log(`Export completed: ${filename}`);
      
      // Show notification about unmapped items if any
      if (unmappedItems.length > 0) {
        const clientIssues = unmappedItems.filter(item => item.reason);
        const skuIssues = unmappedItems.filter(item => item.asin);
        
        let alertMessage = 'Export completed with warnings:\n\n';
        
        if (clientIssues.length > 0) {
          alertMessage += 'Clients not found in database:\n';
          const uniqueClients = [...new Set(clientIssues.map(item => item.client))];
          uniqueClients.forEach(client => {
            alertMessage += `• ${client}\n`;
          });
          alertMessage += '\n';
        }
        
        if (skuIssues.length > 0) {
          alertMessage += `Could not find SKUs for ${skuIssues.length} item(s). ASINs used as fallback:\n`;
          // Group by client for better readability
          const byClient = skuIssues.reduce((acc, item) => {
            if (!acc[item.client]) acc[item.client] = [];
            acc[item.client].push(item);
            return acc;
          }, {});
          
          Object.entries(byClient).forEach(([client, items]) => {
            alertMessage += `\n${client}:\n`;
            items.forEach(item => {
              alertMessage += `  • ${item.shipCode}: ASIN ${item.asin} (${item.quantity} qty) - ${item.description}\n`;
            });
          });
        }
        
        alertMessage += '\nThe export has been completed using ASINs where SKUs could not be found.';
        alert(alertMessage);
      } else {
        console.log('All items successfully mapped to SKUs');
      }
      
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Error exporting data: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to export a single client to CSV format
  const exportClientToCSV = async (clientGroup) => {
    if (!clientGroup.shipments || clientGroup.shipments.length === 0) {
      alert('No shipments to export for this client');
      return;
    }

    try {
      // Show loading state
      setIsLoading(true);

      console.log(`Exporting client: ${clientGroup.clientName}`);
      
      // Remove 'OLD__' prefix from client name if present
      const cleanClientName = clientGroup.clientName.replace(/^OLD__/, '');
      
      // Get client ID using the cleaned name
      const clientId = await getClientId(cleanClientName);
      if (!clientId) {
        alert(`Could not find client "${cleanClientName}" in database. Export cancelled.`);
        return;
      }

      const csvData = {}; // Object to store SKU quantities (SKU as key, quantity as value)
      const unmappedItems = []; // Track items that couldn't be mapped to SKUs
      
      for (const shipment of clientGroup.shipments) {
        console.log(`Processing shipment: ${shipment.ship_code}`);
        
        if (!shipment.items || shipment.items.length === 0) {
          continue; // Skip shipments with no items
        }

        // Process each item in the shipment
        for (const item of shipment.items) {
          // The ASIN is stored in item.sku field from Stallion API
          const asin = item.sku || 'Unknown';
          
          // Get SKU from ASIN
          console.log(`Looking up SKU for ASIN: ${asin}`);
          const sku = await getSkuFromAsin(asin, clientId);
          
          // Track if SKU couldn't be found
          if (!sku) {
            unmappedItems.push({
              client: cleanClientName,
              shipCode: shipment.ship_code,
              asin: asin,
              quantity: item.quantity || 1,
              description: item.description || 'No description'
            });
          }
          
          const finalSku = sku || asin; // Fallback to ASIN if SKU not found
          const quantity = item.quantity || 1;
          
          // Add to csvData (aggregate quantities for duplicate SKUs)
          if (csvData[finalSku]) {
            csvData[finalSku] += quantity;
          } else {
            csvData[finalSku] = quantity;
          }
        }
      }

      // Convert to CSV format
      let csvContent = 'merchant_sku,quantity\n';
      
      // Sort SKUs alphabetically for consistent output
      const sortedSkus = Object.keys(csvData).sort();
      
      for (const sku of sortedSkus) {
        csvContent += `${sku},${csvData[sku]}\n`;
      }

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      // Generate filename using cleaned client name
      const dateStr = new Date().toISOString().slice(0, 10);
      const sanitizedClientName = cleanClientName.replace(/[^a-z0-9]/gi, '_');
      const filename = `stallion_${sanitizedClientName}_${dateStr}.csv`;
      
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`Export completed: ${filename}`);
      
      // Log cache statistics
      const cacheStats = getCacheStats();
      console.log(`Cache statistics: ${cacheStats.validEntries} valid mappings, ${cacheStats.nullEntries} null entries, ${cacheStats.totalEntries} total entries`);
      
      // Show notification about unmapped items if any
      if (unmappedItems.length > 0) {
        let alertMessage = `Export completed for ${cleanClientName} with warnings:\n\n`;
        alertMessage += `Could not find SKUs for ${unmappedItems.length} item(s). ASINs used as fallback:\n\n`;
        
        unmappedItems.forEach(item => {
          alertMessage += `• ${item.shipCode}: ASIN ${item.asin} (${item.quantity} qty) - ${item.description}\n`;
        });
        
        alertMessage += '\nThe export has been completed using ASINs where SKUs could not be found.';
        alert(alertMessage);
      } else {
        alert(`CSV export completed successfully for ${cleanClientName}!\nFile: ${filename}`);
      }
      
    } catch (error) {
      console.error('Error exporting client CSV:', error);
      alert('Error exporting data: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to export entire cache to CSV - fetches fresh data
  const exportEntireCache = async () => {
    setIsExporting(true);
    setError(null);

    try {
      // Fetch fresh data directly from backend with longer timeout
      console.log('Fetching fresh shipment data for export...');
      alert('⏳ Starting fresh data fetch from Stallion API. This may take several minutes...');

      const response = await axios.get(`${API_URL}/api/stallion-export-fresh`, {
        timeout: 600000 // 10 minutes timeout
      });

      // Check response status
      if (response.data.status === 'updating') {
        alert('⚠️ A data update is already in progress. Please wait a few minutes and try again.');
        setIsExporting(false);
        return;
      }

      if (response.data.status === 'error') {
        alert('❌ Error fetching data: ' + response.data.message);
        setIsExporting(false);
        return;
      }

      const freshShipments = Array.isArray(response.data.data) ? response.data.data : [];

      if (freshShipments.length === 0) {
        alert('No shipment data available from the server.');
        setIsExporting(false);
        return;
      }

      console.log(`Fetched ${freshShipments.length} fresh shipments for export`);

      const csvData = [];

      // Process all shipments
      freshShipments.forEach(shipment => {
        // If shipment has no items, add one row with shipment details only
        if (!shipment.items || shipment.items.length === 0) {
          csvData.push({
            'Ship Code': shipment.ship_code || '',
            'Client Name': shipment.clientName || '',
            'Status': shipment.status || '',
            'Tracking Code': shipment.tracking_code || '',
            'Created Date': shipment.created_at ? new Date(shipment.created_at).toLocaleString() : '',
            'Recipient Name': shipment.name || '',
            'Address': normalizeAddress(shipment.address1, shipment.city, shipment.province_code, shipment.postal_code),
            'Phone': shipment.phone || '',
            'Email': shipment.email || '',
            'Weight': shipment.weight ? `${shipment.weight} ${shipment.weight_unit || ''}` : '',
            'Dimensions': shipment.length && shipment.width && shipment.height ?
              `${shipment.length} x ${shipment.width} x ${shipment.height} ${shipment.dimension_unit || ''}` : '',
            'Total Value': formatCurrency(shipment.value).replace('$', ''),
            'Currency': shipment.currency || 'USD',
            'Service Type': shipment.service_type || '',
            'Package Type': shipment.package_type || '',
            'Item SKU/ASIN': '',
            'Item Description': '',
            'Item Quantity': '',
            'Item Value': '',
            'Item Weight': '',
            'Item Dimensions': '',
            'Country of Origin': '',
            'HS Code': '',
            'FNSKU': ''
          });
        } else {
          // Add one row per item
          shipment.items.forEach((item) => {
            csvData.push({
              'Ship Code': shipment.ship_code || '',
              'Client Name': shipment.clientName || '',
              'Status': shipment.status || '',
              'Tracking Code': shipment.tracking_code || '',
              'Created Date': shipment.created_at ? new Date(shipment.created_at).toLocaleString() : '',
              'Recipient Name': shipment.name || '',
              'Address': normalizeAddress(shipment.address1, shipment.city, shipment.province_code, shipment.postal_code),
              'Phone': shipment.phone || '',
              'Email': shipment.email || '',
              'Weight': shipment.weight ? `${shipment.weight} ${shipment.weight_unit || ''}` : '',
              'Dimensions': shipment.length && shipment.width && shipment.height ?
                `${shipment.length} x ${shipment.width} x ${shipment.height} ${shipment.dimension_unit || ''}` : '',
              'Total Value': formatCurrency(shipment.value).replace('$', ''),
              'Currency': shipment.currency || 'USD',
              'Service Type': shipment.service_type || '',
              'Package Type': shipment.package_type || '',
              'Item SKU/ASIN': item.sku || '',
              'Item Description': item.description || '',
              'Item Quantity': item.quantity || '',
              'Item Value': item.value ? formatCurrency(item.value).replace('$', '') : '',
              'Item Weight': item.weight_gm ? `${item.weight_gm}g` : '',
              'Item Dimensions': item.length_mm && item.width_mm && item.height_mm ?
                `${item.length_mm} x ${item.width_mm} x ${item.height_mm} mm` : '',
              'Country of Origin': item.country_of_origin || '',
              'HS Code': item.hs_code || '',
              'FNSKU': item.fnsku || ''
            });
          });
        }
      });

      // Convert to CSV format
      const headers = Object.keys(csvData[0]);
      let csvContent = headers.join(',') + '\n';

      csvData.forEach(row => {
        const values = headers.map(header => {
          let value = row[header];
          // Convert to string and handle null/undefined
          if (value === null || value === undefined) {
            value = '';
          } else {
            value = String(value);
          }
          // Escape commas and quotes in CSV values
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        });
        csvContent += values.join(',') + '\n';
      });

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      const dateStr = new Date().toISOString().slice(0, 10);
      const filename = `stallion_full_cache_export_${dateStr}.csv`;

      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`Full export completed: ${filename}`);
      console.log(`Exported ${csvData.length} rows for ${freshShipments.length} shipments`);

      alert(`✅ Full export completed successfully!\n\nFile: ${filename}\nTotal shipments: ${freshShipments.length}\nTotal rows: ${csvData.length}`);

    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data: ' + error.message);
      setIsExporting(false);
    }
  };

  // Function to export comprehensive ship code details
  const exportComprehensiveShipCodes = () => {
    const inputCodes = parseShipCodes(exportShipCodes);
    if (inputCodes.length === 0) {
      alert('Please enter some ship codes to export');
      return;
    }

    // Ensure allShipments is an array
    if (!Array.isArray(allShipments) || allShipments.length === 0) {
      alert('No shipment data available. Please refresh the data.');
      return;
    }

    setIsExporting(true);

    try {
      // Create a map of ship codes to shipments for faster lookup
      const shipmentMap = new Map();
      allShipments.forEach(shipment => {
        if (shipment.ship_code) {
          shipmentMap.set(shipment.ship_code.toLowerCase(), shipment);
        }
      });

      const csvData = [];
      const notFoundCodes = [];

      // Process each input code
      inputCodes.forEach(code => {
        const normalizedCode = code.toLowerCase();
        const shipment = shipmentMap.get(normalizedCode);

        if (!shipment) {
          notFoundCodes.push(code);
          return;
        }

        // If shipment has no items, add one row with shipment details only
        if (!shipment.items || shipment.items.length === 0) {
          csvData.push({
            'Ship Code': shipment.ship_code || '',
            'Client Name': shipment.clientName || '',
            'Status': shipment.status || '',
            'Tracking Code': shipment.tracking_code || '',
            'Created Date': shipment.created_at ? new Date(shipment.created_at).toLocaleString() : '',
            'Recipient Name': shipment.name || '',
            'Address': normalizeAddress(shipment.address1, shipment.city, shipment.province_code, shipment.postal_code),
            'Phone': shipment.phone || '',
            'Email': shipment.email || '',
            'Weight': shipment.weight ? `${shipment.weight} ${shipment.weight_unit || ''}` : '',
            'Dimensions': shipment.length && shipment.width && shipment.height ?
              `${shipment.length} x ${shipment.width} x ${shipment.height} ${shipment.dimension_unit || ''}` : '',
            'Total Value': formatCurrency(shipment.value).replace('$', ''),
            'Currency': shipment.currency || 'USD',
            'Service Type': shipment.service_type || '',
            'Package Type': shipment.package_type || '',
            'Item SKU/ASIN': '',
            'Item Description': '',
            'Item Quantity': '',
            'Item Value': '',
            'Item Weight': '',
            'Item Dimensions': '',
            'Country of Origin': '',
            'HS Code': '',
            'FNSKU': ''
          });
        } else {
          // Add one row per item - REPEAT ALL SHIPMENT DETAILS FOR EACH ITEM
          shipment.items.forEach((item) => {
            csvData.push({
              'Ship Code': shipment.ship_code || '',
              'Client Name': shipment.clientName || '',
              'Status': shipment.status || '',
              'Tracking Code': shipment.tracking_code || '',
              'Created Date': shipment.created_at ? new Date(shipment.created_at).toLocaleString() : '',
              'Recipient Name': shipment.name || '',
              'Address': normalizeAddress(shipment.address1, shipment.city, shipment.province_code, shipment.postal_code),
              'Phone': shipment.phone || '',
              'Email': shipment.email || '',
              'Weight': shipment.weight ? `${shipment.weight} ${shipment.weight_unit || ''}` : '',
              'Dimensions': shipment.length && shipment.width && shipment.height ?
                `${shipment.length} x ${shipment.width} x ${shipment.height} ${shipment.dimension_unit || ''}` : '',
              'Total Value': formatCurrency(shipment.value).replace('$', ''),
              'Currency': shipment.currency || 'USD',
              'Service Type': shipment.service_type || '',
              'Package Type': shipment.package_type || '',
              'Item SKU/ASIN': item.sku || '',
              'Item Description': item.description || '',
              'Item Quantity': item.quantity || '',
              'Item Value': item.value ? formatCurrency(item.value).replace('$', '') : '',
              'Item Weight': item.weight_gm ? `${item.weight_gm}g` : '',
              'Item Dimensions': item.length_mm && item.width_mm && item.height_mm ?
                `${item.length_mm} x ${item.width_mm} x ${item.height_mm} mm` : '',
              'Country of Origin': item.country_of_origin || '',
              'HS Code': item.hs_code || '',
              'FNSKU': item.fnsku || ''
            });
          });
        }
      });

      // Convert to CSV format
      if (csvData.length === 0) {
        alert('No matching shipments found for the provided ship codes.');
        return;
      }

      const headers = Object.keys(csvData[0]);
      let csvContent = headers.join(',') + '\n';

      csvData.forEach(row => {
        const values = headers.map(header => {
          let value = row[header];
          // Convert to string and handle null/undefined
          if (value === null || value === undefined) {
            value = '';
          } else {
            value = String(value);
          }
          // Escape commas and quotes in CSV values
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        });
        csvContent += values.join(',') + '\n';
      });

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      const dateStr = new Date().toISOString().slice(0, 10);
      const filename = `stallion_comprehensive_export_${dateStr}.csv`;

      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`Comprehensive export completed: ${filename}`);
      console.log(`Exported ${csvData.length} rows for ${inputCodes.length - notFoundCodes.length} shipments`);

      // Show completion message
      let message = `✅ Export completed!\n\nFile: ${filename}\nShipments found: ${inputCodes.length - notFoundCodes.length}\nTotal rows: ${csvData.length}`;

      if (notFoundCodes.length > 0) {
        message += `\n\n⚠️ Ship codes not found (${notFoundCodes.length}):\n${notFoundCodes.join(', ')}`;
      }

      alert(message);

    } catch (error) {
      console.error('Error exporting comprehensive data:', error);
      alert('Error exporting data: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="stallion-status-container">
      <div className="header-section">
        <h1>Stallion Shipment Reconciliation</h1>
        {lastRefreshed && (
          <div className="last-refreshed">
            Data loaded: {formatDate(lastRefreshed)} ({allShipments.length} total shipments)
            <span className="cache-indicator" style={{ 
              marginLeft: '10px', 
              padding: '2px 6px', 
              backgroundColor: getCachedData() ? '#dcfce7' : '#fef3c7',
              color: getCachedData() ? '#166534' : '#92400e',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {getCachedData() ? '📦 Cached' : '🌐 Live'}
            </span>
            <button onClick={handleRefresh} className="refresh-button" disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Refresh Data'}
            </button>
            <button
              onClick={exportEntireCache}
              className="refresh-button"
              style={{ backgroundColor: '#059669', marginLeft: '5px' }}
              disabled={isLoading || isExporting}
              title="Fetch fresh data from server and export all shipments to CSV"
            >
              {isExporting ? '⏳ Exporting...' : '📊 Export All Data (Fresh)'}
            </button>
            {getCachedData() && (
              <button
                onClick={() => {
                  clearCache();
                  setAllShipments([]);
                  setLastRefreshed(null);
                  setError('Cache cleared. Click "Refresh Data" to load fresh data.');
                }}
                className="refresh-button"
                style={{ backgroundColor: '#ef4444', marginLeft: '5px' }}
                disabled={isLoading}
              >
                Clear Cache
              </button>
            )}
            {Object.keys(asinToSkuCache).length > 0 && (
              <button 
                onClick={clearAsinSkuCache}
                className="refresh-button" 
                style={{ backgroundColor: '#f59e0b', marginLeft: '5px' }}
                disabled={isLoading}
                title={`Clear ASIN→SKU cache (${getCacheStats().totalEntries} entries)`}
              >
                Clear SKU Cache
              </button>
            )}
          </div>
        )}
      </div>

      {isInitialLoad && isLoading ? (
        <div className="loading" style={{ textAlign: 'center', padding: '2rem' }}>
          <CircularProgress />
          <p style={{ marginTop: '1rem' }}>Loading all client shipments...</p>
        </div>
      ) : (
        <>
          <div className="reconcile-input-section" style={{ margin: '2rem 0', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
            <h2>Enter Ship Codes to Reconcile</h2>
            <p style={{ color: '#666', marginBottom: '0.5rem' }}>
              Enter ship codes separated by commas, semicolons, or line breaks:
            </p>
            <p style={{ color: '#888', fontSize: '12px', marginBottom: '1rem' }}>
              💾 Data is cached in your browser for 2 hours for faster loading. Use "Refresh Data" to get the latest information.
            </p>
            <textarea
              value={shipCodeInput}
              onChange={(e) => setShipCodeInput(e.target.value)}
              placeholder="Enter ship codes here... (e.g., SH123456, SH789012; SH345678)"
              rows="6"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'monospace'
              }}
            />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '10px' }}>
              <button 
                onClick={handleReconcile}
                disabled={!shipCodeInput.trim() || isLoading}
                className="refresh-button"
                style={{ backgroundColor: '#3b82f6' }}
              >
                Reconcile Ship Codes
              </button>
              <button 
                onClick={handleClear}
                className="refresh-button"
                style={{ backgroundColor: '#6b7280' }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Comprehensive Export Section */}
          <div className="comprehensive-export-section" style={{
            margin: '2rem 0',
            padding: '1rem',
            border: '1px solid #ddd',
            borderRadius: '8px',
            backgroundColor: '#f0f9ff'
          }}>
            <h2>📊 Comprehensive Ship Code Export</h2>
            <p style={{ color: '#666', marginBottom: '0.5rem' }}>
              Export detailed information for specific ship codes including all items, dimensions, tracking, and recipient details:
            </p>
            <p style={{ color: '#888', fontSize: '12px', marginBottom: '1rem' }}>
              📋 This creates a comprehensive CSV with all available ship code data from the Stallion API - perfect for detailed analysis.
            </p>
            <textarea
              value={exportShipCodes}
              onChange={(e) => setExportShipCodes(e.target.value)}
              placeholder="Enter ship codes to export... (e.g., SH123456, SH789012; SH345678)"
              rows="4"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'monospace'
              }}
            />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                onClick={exportComprehensiveShipCodes}
                disabled={!exportShipCodes.trim() || isExporting || !allShipments || allShipments.length === 0}
                className="refresh-button"
                style={{
                  backgroundColor: isExporting ? '#6b7280' : '#0ea5e9',
                  cursor: isExporting ? 'not-allowed' : 'pointer'
                }}
              >
                {isExporting ? '⏳ Exporting...' : '📊 Export Comprehensive CSV'}
              </button>
              <button
                onClick={() => setExportShipCodes('')}
                className="refresh-button"
                style={{ backgroundColor: '#6b7280' }}
              >
                Clear
              </button>
              {exportShipCodes && (
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {parseShipCodes(exportShipCodes).length} ship code(s) to export
                </span>
              )}
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '12px', color: '#666' }}>
              <strong>Export includes:</strong> Ship code, client, status, tracking, dates, recipient info, address, phone, email,
              dimensions, weight, value, service type, package type, and detailed item information (SKU/ASIN, description, quantity,
              value, weight, dimensions, country of origin, HS code, FNSKU)
            </div>
          </div>

          {/* ASIN Filter Section */}
          <div className="asin-filter-section" style={{
            margin: '2rem 0',
            padding: '1rem',
            border: '1px solid #ddd',
            borderRadius: '8px',
            backgroundColor: '#f8fafc'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>🔍 ASIN Filter - FDA vs Non-FDA Ship Codes</h2>
              <button
                onClick={() => {
                  if (!showAsinFilter) {
                    filterShipCodesByAsins();
                  }
                  setShowAsinFilter(!showAsinFilter);
                }}
                disabled={isLoading || !matchedShipments || matchedShipments.length === 0}
                style={{
                  backgroundColor: showAsinFilter ? '#ef4444' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {showAsinFilter ? '❌ Hide Results' : '🔍 Generate Filter'}
              </button>
            </div>

            <p style={{ color: '#666', fontSize: '14px', marginBottom: '1rem' }}>
              Filter your <strong>reconciled ship codes</strong> to find which ones contain specific ASINs categorized as FDA or Non-FDA items.
              <br />
              <span style={{ color: '#ef4444', fontSize: '13px' }}>
                ⚠️ You must reconcile ship codes first before using this filter.
              </span>
            </p>

            {showAsinFilter && asinFilterResults && (
              <div style={{ marginTop: '1.5rem' }}>
                {asinFilterResults.error ? (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '4px',
                    color: '#dc2626'
                  }}>
                    {asinFilterResults.error}
                  </div>
                ) : (
                  <>
                    {/* Summary */}
                    <div style={{
                      display: 'flex',
                      gap: '2rem',
                      marginBottom: '1.5rem',
                      padding: '1rem',
                      backgroundColor: '#fff',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>
                          {asinFilterResults.nonFdaShipCodes.length}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>Non-FDA Ship Codes</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#059669' }}>
                          {asinFilterResults.fdaShipCodes.length}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>FDA Ship Codes</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
                          {asinFilterResults.nonFdaShipCodes.length + asinFilterResults.fdaShipCodes.length}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Ship Codes</div>
                      </div>
                    </div>

                    {/* Non-FDA Ship Codes */}
                    <div style={{ marginBottom: '2rem' }}>
                      <h3 style={{ color: '#ef4444', marginBottom: '1rem' }}>
                        🚫 Non-FDA Ship Codes ({asinFilterResults.nonFdaShipCodes.length})
                      </h3>
                      {asinFilterResults.nonFdaShipCodes.length > 0 ? (
                        <div style={{
                          width: '100%',
                          overflowX: 'auto',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}>
                          <table style={{
                            width: '100%',
                            minWidth: '1000px',
                            backgroundColor: 'white',
                            borderCollapse: 'collapse'
                          }}>
                            <thead>
                              <tr style={{ backgroundColor: '#fef2f2' }}>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', minWidth: '120px' }}>Ship Code</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', minWidth: '150px' }}>Client</th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', minWidth: '100px' }}>Status</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', minWidth: '200px' }}>Matching ASINs</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', minWidth: '130px' }}>Tracking Code</th>
                              </tr>
                            </thead>
                            <tbody>
                              {asinFilterResults.nonFdaShipCodes.map((result, index) => (
                                <tr key={result.shipCode} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                  <td style={{ padding: '12px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                                    {result.shipCode}
                                  </td>
                                  <td style={{ padding: '12px' }}>{result.clientName}</td>
                                  <td style={{ padding: '12px', textAlign: 'center' }}>
                                    <span style={{
                                      backgroundColor: result.status === 'delivered' ? '#dcfce7' :
                                                     result.status === 'processing' ? '#fef3c7' : '#fee2e2',
                                      color: result.status === 'delivered' ? '#166534' :
                                             result.status === 'processing' ? '#92400e' : '#991b1b',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: 'bold'
                                    }}>
                                      {result.status || 'Unknown'}
                                    </span>
                                  </td>
                                  <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px' }}>
                                    {result.matchingAsins.join(', ')}
                                  </td>
                                  <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px' }}>
                                    {result.trackingCode || 'N/A'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={{ padding: '1rem', color: '#6b7280', fontStyle: 'italic' }}>
                          No ship codes found with Non-FDA ASINs.
                        </div>
                      )}
                    </div>

                    {/* FDA Ship Codes */}
                    <div>
                      <h3 style={{ color: '#059669', marginBottom: '1rem' }}>
                        ✅ FDA Ship Codes ({asinFilterResults.fdaShipCodes.length})
                      </h3>
                      {asinFilterResults.fdaShipCodes.length > 0 ? (
                        <div style={{
                          width: '100%',
                          overflowX: 'auto',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}>
                          <table style={{
                            width: '100%',
                            minWidth: '1000px',
                            backgroundColor: 'white',
                            borderCollapse: 'collapse'
                          }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f0fdf4' }}>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', minWidth: '120px' }}>Ship Code</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', minWidth: '150px' }}>Client</th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', minWidth: '100px' }}>Status</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', minWidth: '200px' }}>Matching ASINs</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', minWidth: '130px' }}>Tracking Code</th>
                              </tr>
                            </thead>
                            <tbody>
                              {asinFilterResults.fdaShipCodes.map((result, index) => (
                                <tr key={result.shipCode} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                  <td style={{ padding: '12px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                                    {result.shipCode}
                                  </td>
                                  <td style={{ padding: '12px' }}>{result.clientName}</td>
                                  <td style={{ padding: '12px', textAlign: 'center' }}>
                                    <span style={{
                                      backgroundColor: result.status === 'delivered' ? '#dcfce7' :
                                                     result.status === 'processing' ? '#fef3c7' : '#fee2e2',
                                      color: result.status === 'delivered' ? '#166534' :
                                             result.status === 'processing' ? '#92400e' : '#991b1b',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: 'bold'
                                    }}>
                                      {result.status || 'Unknown'}
                                    </span>
                                  </td>
                                  <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px' }}>
                                    {result.matchingAsins.join(', ')}
                                  </td>
                                  <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px' }}>
                                    {result.trackingCode || 'N/A'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={{ padding: '1rem', color: '#6b7280', fontStyle: 'italic' }}>
                          No ship codes found with FDA ASINs.
                        </div>
                      )}
                    </div>

                    {/* Export buttons for filtered results */}
                    <div style={{
                      marginTop: '2rem',
                      padding: '1rem',
                      backgroundColor: '#f1f5f9',
                      borderRadius: '6px',
                      display: 'flex',
                      gap: '1rem',
                      justifyContent: 'center'
                    }}>
                      <button
                        onClick={() => {
                          const csvContent = "Ship Code,Client,Status,Matching ASINs,Tracking Code\n" +
                            asinFilterResults.nonFdaShipCodes.map(row =>
                              `${row.shipCode},${row.clientName},${row.status},${row.matchingAsins.join(';')},${row.trackingCode || ''}`
                            ).join('\n');

                          const blob = new Blob([csvContent], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `non_fda_ship_codes_${new Date().toISOString().slice(0, 10)}.csv`;
                          link.click();
                          URL.revokeObjectURL(url);
                        }}
                        style={{
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        📄 Export Non-FDA CSV
                      </button>

                      <button
                        onClick={() => {
                          const csvContent = "Ship Code,Client,Status,Matching ASINs,Tracking Code\n" +
                            asinFilterResults.fdaShipCodes.map(row =>
                              `${row.shipCode},${row.clientName},${row.status},${row.matchingAsins.join(';')},${row.trackingCode || ''}`
                            ).join('\n');

                          const blob = new Blob([csvContent], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `fda_ship_codes_${new Date().toISOString().slice(0, 10)}.csv`;
                          link.click();
                          URL.revokeObjectURL(url);
                        }}
                        style={{
                          backgroundColor: '#059669',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        📄 Export FDA CSV
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="error-message" style={{ margin: '1rem 0', padding: '1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', color: '#dc2626' }}>
              {error}
            </div>
          )}

          {(matchedShipments.length > 0 || unmatchedCodes.length > 0) && (
            <div className="reconcile-results">
              <div className="results-summary" style={{ margin: '1rem 0', padding: '1rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                <h3>Reconciliation Results</h3>
                <div style={{ display: 'flex', gap: '2rem', marginTop: '0.5rem' }}>
                  <span style={{ color: '#10b981', fontWeight: 'bold' }}>
                    ✓ Matched: {matchedShipments.length}
                  </span>
                  <span style={{ color: '#ef4444', fontWeight: 'bold' }}>
                    ✗ Not Found: {unmatchedCodes.length}
                  </span>
                </div>
                
                {unmatchedCodes.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <h4 style={{ color: '#ef4444', marginBottom: '0.5rem' }}>Ship codes not found:</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {unmatchedCodes.map((code, index) => (
                        <span key={index} style={{ 
                          backgroundColor: '#fef2f2', 
                          color: '#dc2626', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '4px',
                          fontSize: '12px',
                          border: '1px solid #fecaca'
                        }}>
                          {code}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {matchedShipments.length > 0 && (
                <div className="matched-shipments" style={{ marginBottom: '5rem' }}>
                  <div style={{ margin: '1rem 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3>Matched Shipments ({matchedShipments.length})</h3>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: '0.5rem 0' }}>
                        Use the "Export" button in each client section to export shipments per client.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAsinSummary(!showAsinSummary)}
                      style={{
                        backgroundColor: showAsinSummary ? '#2563eb' : '#6b7280',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {showAsinSummary ? '📋 Hide ASIN Summary' : '📊 Show ASIN Summary'}
                    </button>
                  </div>

                  {/* ASIN Summary Section */}
                  {showAsinSummary && matchedShipments && matchedShipments.length > 0 && (
                    <div style={{
                      margin: '1.5rem 0',
                      padding: '1.5rem',
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}>
                      <h3 style={{ marginBottom: '1rem', color: '#1e293b' }}>
                        📊 ASIN Summary - Aggregate Unit Counts
                      </h3>
                      {(() => {
                        const asinSummary = getAsinSummary();

                        // Check if we have any ASIN data
                        if (!asinSummary || asinSummary.length === 0) {
                          return (
                            <div style={{ padding: '1rem', color: '#6b7280', fontStyle: 'italic' }}>
                              No ASIN data available in the matched shipments.
                            </div>
                          );
                        }

                        const totalUnits = asinSummary.reduce((sum, item) => sum + item.totalQuantity, 0);
                        const uniqueAsins = asinSummary.length;

                        return (
                          <>
                            <div style={{
                              display: 'flex',
                              gap: '2rem',
                              marginBottom: '1rem',
                              padding: '0.5rem',
                              backgroundColor: '#fff',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}>
                              <span><strong>Total Unique ASINs:</strong> {uniqueAsins}</span>
                              <span><strong>Total Units:</strong> {totalUnits.toLocaleString()}</span>
                            </div>

                            <div style={{ overflowX: 'auto' }}>
                              <table style={{
                                width: '100%',
                                backgroundColor: 'white',
                                borderCollapse: 'collapse',
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px'
                              }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f1f5f9' }}>
                                    <th style={{
                                      padding: '12px',
                                      textAlign: 'left',
                                      borderBottom: '2px solid #e5e7eb',
                                      width: '40px'
                                    }}></th>
                                    <th style={{
                                      padding: '12px',
                                      textAlign: 'left',
                                      borderBottom: '2px solid #e5e7eb',
                                      minWidth: '120px'
                                    }}>ASIN</th>
                                    <th style={{
                                      padding: '12px',
                                      textAlign: 'left',
                                      borderBottom: '2px solid #e5e7eb',
                                      minWidth: '200px'
                                    }}>Description</th>
                                    <th style={{
                                      padding: '12px',
                                      textAlign: 'center',
                                      borderBottom: '2px solid #e5e7eb',
                                      minWidth: '100px'
                                    }}>Total Qty</th>
                                    <th style={{
                                      padding: '12px',
                                      textAlign: 'center',
                                      borderBottom: '2px solid #e5e7eb',
                                      minWidth: '100px'
                                    }}>Clients</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {asinSummary.map((asinItem) => {
                                    const isExpanded = expandedAsins.has(asinItem.asin);
                                    const clientCount = Object.keys(asinItem.clients).length;
                                    const hasMultipleClients = clientCount > 1;

                                    return (
                                      <React.Fragment key={asinItem.asin}>
                                        <tr style={{
                                          borderBottom: '1px solid #e5e7eb',
                                          backgroundColor: hasMultipleClients ? '#fef3c7' : 'white'
                                        }}>
                                          <td style={{
                                            padding: '12px',
                                            textAlign: 'center'
                                          }}>
                                            {clientCount > 0 && (
                                              <button
                                                onClick={() => {
                                                  const newExpanded = new Set(expandedAsins);
                                                  if (newExpanded.has(asinItem.asin)) {
                                                    newExpanded.delete(asinItem.asin);
                                                  } else {
                                                    newExpanded.add(asinItem.asin);
                                                  }
                                                  setExpandedAsins(newExpanded);
                                                }}
                                                style={{
                                                  border: '1px solid #d1d5db',
                                                  cursor: 'pointer',
                                                  fontSize: '14px',
                                                  padding: '4px 8px',
                                                  borderRadius: '4px',
                                                  backgroundColor: isExpanded ? '#2563eb' : '#f9fafb',
                                                  color: isExpanded ? '#ffffff' : '#374151',
                                                  fontWeight: 'bold'
                                                }}
                                              >
                                                {isExpanded ? '−' : '+'}
                                              </button>
                                            )}
                                          </td>
                                          <td style={{
                                            padding: '12px',
                                            fontFamily: 'monospace',
                                            fontSize: '13px',
                                            fontWeight: 'bold'
                                          }}>{asinItem.asin}</td>
                                          <td style={{
                                            padding: '12px',
                                            fontSize: '13px'
                                          }}>{asinItem.description}</td>
                                          <td style={{
                                            padding: '12px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                            color: '#059669'
                                          }}>{asinItem.totalQuantity.toLocaleString()}</td>
                                          <td style={{
                                            padding: '12px',
                                            textAlign: 'center'
                                          }}>
                                            <span style={{
                                              backgroundColor: hasMultipleClients ? '#fbbf24' : '#60a5fa',
                                              color: hasMultipleClients ? '#78350f' : '#1e3a8a',
                                              padding: '4px 8px',
                                              borderRadius: '4px',
                                              fontSize: '12px',
                                              fontWeight: 'bold'
                                            }}>
                                              {clientCount} {clientCount === 1 ? 'client' : 'clients'}
                                            </span>
                                          </td>
                                        </tr>
                                        {isExpanded && (
                                          <tr>
                                            <td colSpan="5" style={{
                                              padding: '0',
                                              backgroundColor: '#f8fafc'
                                            }}>
                                              <div style={{ padding: '16px 16px 16px 60px' }}>
                                                <h5 style={{
                                                  marginBottom: '12px',
                                                  color: '#475569',
                                                  fontSize: '14px'
                                                }}>
                                                  Client Breakdown:
                                                </h5>
                                                <table style={{
                                                  width: '100%',
                                                  backgroundColor: 'white',
                                                  border: '1px solid #e5e7eb',
                                                  borderRadius: '4px'
                                                }}>
                                                  <thead>
                                                    <tr style={{ backgroundColor: '#f9fafb' }}>
                                                      <th style={{
                                                        padding: '8px',
                                                        textAlign: 'left',
                                                        borderBottom: '1px solid #e5e7eb',
                                                        fontSize: '13px'
                                                      }}>Client Name</th>
                                                      <th style={{
                                                        padding: '8px',
                                                        textAlign: 'center',
                                                        borderBottom: '1px solid #e5e7eb',
                                                        fontSize: '13px'
                                                      }}>Quantity</th>
                                                      <th style={{
                                                        padding: '8px',
                                                        textAlign: 'center',
                                                        borderBottom: '1px solid #e5e7eb',
                                                        fontSize: '13px'
                                                      }}>% of Total</th>
                                                      <th style={{
                                                        padding: '8px',
                                                        textAlign: 'left',
                                                        borderBottom: '1px solid #e5e7eb',
                                                        fontSize: '13px'
                                                      }}>Ship Codes</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {Object.entries(asinItem.clients)
                                                      .sort((a, b) => b[1].quantity - a[1].quantity)
                                                      .map(([clientName, clientData], idx) => {
                                                        const percentage = ((clientData.quantity / asinItem.totalQuantity) * 100).toFixed(1);
                                                        return (
                                                          <tr key={clientName} style={{
                                                            borderBottom: idx < Object.keys(asinItem.clients).length - 1 ? '1px solid #e5e7eb' : 'none'
                                                          }}>
                                                            <td style={{
                                                              padding: '8px',
                                                              fontWeight: '500',
                                                              fontSize: '13px'
                                                            }}>
                                                              {clientName}
                                                            </td>
                                                            <td style={{
                                                              padding: '8px',
                                                              textAlign: 'center',
                                                              fontWeight: 'bold',
                                                              color: '#059669',
                                                              fontSize: '13px'
                                                            }}>
                                                              {clientData.quantity.toLocaleString()}
                                                            </td>
                                                            <td style={{
                                                              padding: '8px',
                                                              textAlign: 'center',
                                                              fontSize: '13px'
                                                            }}>
                                                              <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '8px'
                                                              }}>
                                                                <div style={{
                                                                  width: '60px',
                                                                  height: '8px',
                                                                  backgroundColor: '#e5e7eb',
                                                                  borderRadius: '4px',
                                                                  overflow: 'hidden'
                                                                }}>
                                                                  <div style={{
                                                                    width: `${percentage}%`,
                                                                    height: '100%',
                                                                    backgroundColor: '#3b82f6'
                                                                  }}></div>
                                                                </div>
                                                                <span>{percentage}%</span>
                                                              </div>
                                                            </td>
                                                            <td style={{
                                                              padding: '8px',
                                                              fontSize: '11px',
                                                              fontFamily: 'monospace',
                                                              color: '#6b7280',
                                                              maxWidth: '300px',
                                                              wordWrap: 'break-word'
                                                            }}>
                                                              {clientData.shipments.join(', ')}
                                                            </td>
                                                          </tr>
                                                        );
                                                      })}
                                                  </tbody>
                                                </table>
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  <div className="excel-table-container" style={{ 
                    width: '80vw', 
                    margin: '0 auto',
                    overflowX: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}>
                    <table className="excel-table" style={{ 
                      width: '100%', 
                      minWidth: '1200px',
                      borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8fafc' }}>
                          <th style={{ 
                            width: '40px', 
                            padding: '12px 8px',
                            textAlign: 'center',
                            borderBottom: '2px solid #e5e7eb',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}></th>
                          <th onClick={() => handleSort('ship_code')} style={{
                            width: '120px',
                            padding: '12px 8px',
                            textAlign: 'left',
                            borderBottom: '2px solid #e5e7eb',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#374151',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}>
                            Ship Code {getSortIndicator('ship_code')}
                          </th>
                          <th onClick={() => handleSort('status')} style={{
                            width: '100px',
                            padding: '12px 8px',
                            textAlign: 'center',
                            borderBottom: '2px solid #e5e7eb',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#374151',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}>
                            Status {getSortIndicator('status')}
                          </th>
                          <th onClick={() => handleSort('created_at')} style={{
                            width: '140px',
                            padding: '12px 8px',
                            textAlign: 'left',
                            borderBottom: '2px solid #e5e7eb',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#374151',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}>
                            Created {getSortIndicator('created_at')}
                          </th>
                          <th onClick={() => handleSort('tracking_code')} style={{
                            width: '130px',
                            padding: '12px 8px',
                            textAlign: 'center',
                            borderBottom: '2px solid #e5e7eb',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#374151',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}>
                            Tracking Code {getSortIndicator('tracking_code')}
                          </th>
                          <th onClick={() => handleSort('weight')} style={{
                            width: '90px',
                            padding: '12px 8px',
                            textAlign: 'center',
                            borderBottom: '2px solid #e5e7eb',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#374151',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}>
                            Weight {getSortIndicator('weight')}
                          </th>
                          <th onClick={() => handleSort('value')} style={{
                            width: '100px',
                            padding: '12px 8px',
                            textAlign: 'right',
                            borderBottom: '2px solid #e5e7eb',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#374151',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}>
                            Value {getSortIndicator('value')}
                          </th>
                          <th onClick={() => handleSort('name')} style={{
                            width: '150px',
                            padding: '12px 8px',
                            textAlign: 'left',
                            borderBottom: '2px solid #e5e7eb',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#374151',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}>
                            Recipient {getSortIndicator('name')}
                          </th>
                          <th onClick={() => handleSort('address1')} style={{
                            minWidth: '200px',
                            padding: '12px 8px',
                            textAlign: 'left',
                            borderBottom: '2px solid #e5e7eb',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#374151',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}>
                            Address {getSortIndicator('address1')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getShipmentsGroupedByClient().map((clientGroup) => (
                          <React.Fragment key={clientGroup.clientName}>
                            {/* Client Header Row */}
                            <tr style={{ backgroundColor: '#1e40af', color: 'white' }}>
                              <td colSpan="9" style={{ 
                                padding: '12px 16px', 
                                fontWeight: 'bold', 
                                fontSize: '16px',
                                borderTop: '2px solid #1e40af',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <div>
                                  📦 {clientGroup.clientName} 
                                  <span style={{ 
                                    marginLeft: '16px', 
                                    fontSize: '14px', 
                                    opacity: '0.9' 
                                  }}>
                                    {clientGroup.count} shipment{clientGroup.count !== 1 ? 's' : ''} • Total Value: {formatCurrency(clientGroup.totalValue)}
                                  </span>
                                </div>
                                <button 
                                  onClick={() => exportClientToCSV(clientGroup)}
                                  disabled={isLoading}
                                  style={{
                                    backgroundColor: isLoading ? '#6b7280' : '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    padding: '6px 12px',
                                    borderRadius: '4px',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isLoading) {
                                      e.target.style.backgroundColor = '#059669';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isLoading) {
                                      e.target.style.backgroundColor = '#10b981';
                                    }
                                  }}
                                >
                                  📄 Export CSV
                                </button>
                              </td>
                            </tr>
                            {/* Client Shipments */}
                            {clientGroup.shipments.map((shipment) => {
                              const itemsWithOrigins = getItemsWithOrigins(shipment.items);
                              const isExpanded = expandedShipments.has(shipment.ship_code);
                              
                              return (
                                <React.Fragment key={shipment.ship_code}>
                                  <tr className="shipment-main-row" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                                    <td style={{ 
                                      padding: '12px 8px', 
                                      textAlign: 'center',
                                      borderRight: '1px solid #f3f4f6'
                                    }}>
                                      <button
                                        onClick={() => toggleShipmentExpansion(shipment.ship_code)}
                                        style={{
                                          border: '1px solid #d1d5db',
                                          cursor: 'pointer',
                                          fontSize: '14px',
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          backgroundColor: isExpanded ? '#2563eb' : '#f9fafb',
                                          color: isExpanded ? '#ffffff' : '#374151',
                                          fontWeight: 'bold',
                                          minWidth: '28px'
                                        }}
                                      >
                                        {isExpanded ? '−' : '+'}
                                      </button>
                                    </td>
                                    <td style={{ 
                                      padding: '12px 8px', 
                                      fontWeight: 'bold',
                                      fontSize: '13px',
                                      fontFamily: 'monospace',
                                      textAlign: 'left',
                                      borderRight: '1px solid #f3f4f6'
                                    }}>{shipment.ship_code || 'N/A'}</td>
                                    <td style={{ 
                                      padding: '12px 8px',
                                      textAlign: 'center',
                                      borderRight: '1px solid #f3f4f6'
                                    }}>
                                      <span style={{ 
                                        backgroundColor: shipment.status === 'delivered' ? '#dcfce7' : 
                                                       shipment.status === 'processing' ? '#fef3c7' : '#fee2e2',
                                        color: shipment.status === 'delivered' ? '#166534' : 
                                               shipment.status === 'processing' ? '#92400e' : '#991b1b',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: 'bold'
                                      }}>
                                        {shipment.status || 'Unknown'}
                                      </span>
                                    </td>
                                    <td style={{ 
                                      padding: '12px 8px',
                                      fontSize: '12px',
                                      textAlign: 'left',
                                      borderRight: '1px solid #f3f4f6'
                                    }}>{formatDate(shipment.created_at)}</td>
                                    <td style={{ 
                                      padding: '12px 8px',
                                      fontFamily: 'monospace', 
                                      fontSize: '12px',
                                      textAlign: 'center',
                                      borderRight: '1px solid #f3f4f6'
                                    }}>{shipment.tracking_code || 'N/A'}</td>
                                    <td style={{ 
                                      padding: '12px 8px',
                                      fontSize: '12px',
                                      textAlign: 'center',
                                      borderRight: '1px solid #f3f4f6'
                                    }}>{`${shipment.weight || 'N/A'} ${shipment.weight_unit || ''}`}</td>
                                    <td style={{ 
                                      padding: '12px 8px',
                                      fontWeight: 'bold', 
                                      color: '#059669',
                                      fontSize: '13px',
                                      textAlign: 'right',
                                      borderRight: '1px solid #f3f4f6'
                                    }}>{formatCurrency(shipment.value)}</td>
                                    <td style={{ 
                                      padding: '12px 8px',
                                      fontSize: '12px',
                                      textAlign: 'left',
                                      borderRight: '1px solid #f3f4f6'
                                    }}>{shipment.name || 'N/A'}</td>
                                    <td style={{ 
                                      padding: '12px 8px',
                                      fontSize: '11px',
                                      textAlign: 'left',
                                      color: '#6b7280'
                                    }}>
                                      {normalizeAddress(
                                        shipment.address1,
                                        shipment.city,
                                        shipment.province_code,
                                        shipment.postal_code
                                      )}
                                    </td>
                                  </tr>
                                  {isExpanded && (
                                    <tr className="shipment-items-row">
                                      <td colSpan="9" style={{ padding: '0', backgroundColor: '#f1f5f9' }}>
                                        <div style={{ padding: '16px', borderTop: '1px solid #e2e8f0' }}>
                                          <h4 style={{ margin: '0 0 12px 0', color: '#374151', fontSize: '14px' }}>
                                            Items in Shipment ({itemsWithOrigins.length})
                                          </h4>
                                          {itemsWithOrigins.length > 0 ? (
                                            <table style={{ 
                                              width: '100%', 
                                              border: '1px solid #d1d5db', 
                                              borderRadius: '6px',
                                              backgroundColor: 'white',
                                              fontSize: '13px'
                                            }}>
                                              <thead style={{ backgroundColor: '#f3f4f6' }}>
                                                <tr>
                                                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>SKU</th>
                                                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Description</th>
                                                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #d1d5db' }}>Qty</th>
                                                  <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #d1d5db' }}>Value</th>
                                                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #d1d5db' }}>Origin</th>
                                                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #d1d5db' }}>HS Code</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {itemsWithOrigins.map((item, idx) => (
                                                  <tr key={idx} style={{ borderBottom: idx < itemsWithOrigins.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                                    <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '12px' }}>{item.sku}</td>
                                                    <td style={{ padding: '8px' }}>{item.description}</td>
                                                    <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>{item.quantity}</td>
                                                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#059669' }}>{item.value}</td>
                                                    <td style={{ padding: '8px', textAlign: 'center' }}>{item.origin}</td>
                                                    <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'monospace', fontSize: '11px' }}>{item.hsCode}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          ) : (
                                            <div style={{ color: '#6b7280', fontStyle: 'italic' }}>No items found</div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StallionReconcile;